<!-- src/components/modal/estimates-modal.vue -->
<script setup>
import { ref, reactive, computed, onMounted, nextTick } from "vue";
import * as bootstrap from "bootstrap";
import { useEstimateStore } from "@/stores/estimateStore";
import { useCrmClientStore } from "@/stores/crmClientStore";
import { useProjectStore } from "@/stores/projectStore";
import { useTaxStore } from "@/stores/taxStore";
import * as Toast from "@/utils/toast";

/* ---------------- Toast helpers ---------------- */
const notifyError = (msg) => {
  if (Toast?.toast?.error) return Toast.toast.error(msg);
  if (typeof Toast?.toast === "function") return Toast.toast(msg, "error");
  if (Toast?.error) return Toast.error(msg);
  if (typeof Toast === "function") return Toast(msg, "error");
  console.error(msg);
};
const notifySuccess = (msg) => {
  if (Toast?.toast?.success) return Toast.toast.success(msg);
  if (typeof Toast?.toast === "function") return Toast.toast(msg, "success");
  if (Toast?.success) return Toast.success(msg);
  if (typeof Toast === "function") return Toast(msg, "success");
  console.log(msg);
};

/* ---------------- STORES ---------------- */
const estimateStore = useEstimateStore();
const clientStore = useCrmClientStore();
const projectStore = useProjectStore();
const taxStore = useTaxStore?.() ?? null;

/* ---------------- MODAL INSTANCES ---------------- */
const addModalEl = ref(null);
const deleteModalEl = ref(null);
let addModalInstance = null;
let deleteModalInstance = null;

/* ---------------- STATE ---------------- */
const isEdit = ref(false);
const saving = ref(false);
const deletingId = ref(null);
const clientsLoading = ref(false);
const taxesLoading = ref(false);
const projectsLoading = ref(false);
const attempted = ref(false);

/** Date pickers */
const estimateDate = ref(new Date());
const expiryDate = ref(new Date());

/** Main form */
const form = reactive({
  id: null,
  client_id: null,
  client_name: "",
  project_id: null,
  project_name: "",
  email: "",
  client_address: "",
  billing_address: "",
  tax_id: null,
  tax_name: "",
  tax_rate: 0,
  notes: "",
  status: "sent",
  items: [
    {
      item: "",
      description: "",
      qty: 1,
      rate: 0,
      discount: 0,
      total: 0,
    },
  ],
  sub_total: 0,
  discount_total: 0,
  tax_total: 0,
  grand_total: 0,
});

/* ---------------- CLIENT DROPDOWN ---------------- */
const clientQuery = ref("");
const clientOptions = ref([]);
const selectedClient = ref(null);

/* ---------------- PROJECT DROPDOWN ---------------- */
const projectQuery = ref("");
const projectOptions = ref([]);
const selectedProject = ref(null);

/* ---------------- TAX DROPDOWN ---------------- */
const taxQuery = ref("");
const taxOptions = ref([]);
const selectedTax = ref(null);

/* Dropdown open flags (custom dropdown UI) */
const ddClientOpen = ref(false);
const ddProjectOpen = ref(false);
const ddTaxOpen = ref(false);

const clientInputRef = ref(null);
const projectInputRef = ref(null);
const taxInputRef = ref(null);

/* ---------------- HELPERS ---------------- */
function resetForm() {
  attempted.value = false;

  form.id = null;
  form.client_id = null;
  form.client_name = "";
  form.project_id = null;
  form.project_name = "";
  form.email = "";
  form.client_address = "";
  form.billing_address = "";
  form.tax_id = null;
  form.tax_name = "";
  form.tax_rate = 0;
  form.notes = "";
  form.status = "Sent";

  form.items = [
    {
      item: "",
      description: "",
      qty: 1,
      rate: 0,
      discount: 0,
      total: 0,
    },
  ];

  form.sub_total = 0;
  form.discount_total = 0;
  form.tax_total = 0;
  form.grand_total = 0;

  estimateDate.value = new Date();
  expiryDate.value = new Date();

  selectedClient.value = null;
  selectedProject.value = null;
  selectedTax.value = null;
  clientQuery.value = "";
  projectQuery.value = "";
  taxQuery.value = "";

  ddClientOpen.value = false;
  ddProjectOpen.value = false;
  ddTaxOpen.value = false;
}

function fromYmd(value) {
  if (!value) return new Date();
  const [y, m, d] = String(value).split("-");
  const date = new Date(Number(y), Number(m) - 1, Number(d));
  return Number.isNaN(date.getTime()) ? new Date() : date;
}

function toYmd(value) {
  if (!value) return null;
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return null;

  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function formatMoney(value) {
  const num = Number(value || 0);
  return num.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

/* ---------------- LINE ITEMS + TOTALS ---------------- */
function addItemRow() {
  form.items.push({
    item: "",
    description: "",
    qty: 1,
    rate: 0,
    discount: 0,
    total: 0,
  });

  nextTick(() => {
    const el = document.querySelector(".dq-est-last-desc");
    el?.focus?.();
  });
}

function removeItemRow(index) {
  if (form.items.length === 1) return;
  form.items.splice(index, 1);
  computeTotals();
}

function computeRowTotal(item) {
  const qty = Number(item.qty || 0);
  const rate = Number(item.rate || 0);
  const discount = Number(item.discount || 0);
  const base = qty * rate;
  item.total = Math.max(0, base - discount);
}

function computeTotals() {
  let sub = 0;
  let disc = 0;

  form.items.forEach((item) => {
    const qty = Number(item.qty || 0);
    const rate = Number(item.rate || 0);
    const discount = Number(item.discount || 0);
    sub += qty * rate;
    disc += discount;
  });

  const taxable = Math.max(0, sub - disc);
  const tax = form.tax_id ? (taxable * Number(form.tax_rate || 0)) / 100 : 0;
  const grand = taxable + tax;

  form.sub_total = sub;
  form.discount_total = disc;
  form.tax_total = tax;
  form.grand_total = grand;
}

const grandTotalLabel = computed(() => formatMoney(form.grand_total));

/* ---------------- VALIDATION UX ---------------- */
const errors = computed(() => {
  const e = {};
  if (!form.client_id) e.client_id = "Please select a client.";
  if (!form.email?.trim()) e.email = "Client email is required.";
  const hasLine = form.items.some(
    (i) => (i.description || "").trim() || (i.item || "").trim()
  );
  if (!hasLine) e.items = "Add at least one item/description.";
  return e;
});

const canSubmit = computed(
  () => !saving.value && Object.keys(errors.value).length === 0
);

/* ---------------- CLIENT LOGIC ---------------- */
async function loadClientOptions(q = "") {
  try {
    clientsLoading.value = true;
    const res = await clientStore.list({ q, per_page: 10, paginate: true });
    const arr = res?.data?.data || clientStore.clients || [];
    clientOptions.value = (arr || []).map((c) => ({
      id: c.id,
      label: c.name || c.client_name || "Unnamed client",
      email: c.email || "",
      address: c.address || "",
      phone: c.phone || "",
    }));
  } catch (e) {
    console.error("Failed to load clients", e);
  } finally {
    clientsLoading.value = false;
  }
}

function chooseClient(option) {
  selectedClient.value = option;
  form.client_id = option.id;
  form.client_name = option.label;
  if (!form.email) form.email = option.email || "";
  if (!form.client_address) form.client_address = option.address || "";

  ddClientOpen.value = false;

  // when client changes, reload projects
  loadProjectOptions(option.id).catch((e) => console.error("projects", e));
}

/* ---------------- PROJECT LOGIC ---------------- */
async function loadProjectOptions(clientId, q = "") {
  if (!clientId) {
    projectOptions.value = [];
    selectedProject.value = null;
    form.project_id = null;
    form.project_name = "";
    return;
  }

  try {
    projectsLoading.value = true;
    await projectStore.list({
      client_id: clientId,
      q,
      paginate: false,
      per_page: 100,
    });
    projectOptions.value = (projectStore.items || []).map((p) => ({
      id: p.id,
      label: `${p.project_code || ""} ${p.name || ""}`.trim(),
    }));
  } catch (e) {
    console.error("Failed to load projects", e);
  } finally {
    projectsLoading.value = false;
  }
}

function chooseProject(option) {
  selectedProject.value = option;
  form.project_id = option.id;
  form.project_name = option.label;
  ddProjectOpen.value = false;
}

/* ---------------- TAX LOGIC ---------------- */
async function loadTaxOptions(q = "") {
  if (!taxStore) return;

  try {
    taxesLoading.value = true;
    const res = (await taxStore.fetch)
      ? await taxStore.fetch({ q, per_page: 50 })
      : await taxStore.list({ q, per_page: 50 });

    const arr =
      (res && res.data && res.data.data) ||
      taxStore.items ||
      taxStore.taxes ||
      [];
    taxOptions.value = (arr || []).map((t) => ({
      id: t.id,
      name: t.name,
      rate: Number(t.rate || t.tax_rate || 0),
      label: `${t.name} (${Number(t.rate || t.tax_rate || 0)}%)`,
      description: t.description || "",
    }));
  } catch (e) {
    console.error("Failed to load taxes", e);
  } finally {
    taxesLoading.value = false;
  }
}

function chooseTax(option) {
  selectedTax.value = option;
  form.tax_id = option.id;
  form.tax_name = option.name;
  form.tax_rate = option.rate;
  ddTaxOpen.value = false;
  computeTotals();
}

function clearTax() {
  selectedTax.value = null;
  form.tax_id = null;
  form.tax_name = "";
  form.tax_rate = 0;
  ddTaxOpen.value = false;
  computeTotals();
}

/* ---------------- SUBMIT ---------------- */
async function submit() {
  attempted.value = true;

  if (!canSubmit.value) {
    const first =
      errors.value.client_id ||
      errors.value.email ||
      errors.value.items ||
      "Please fix the form errors.";
    notifyError(first);
    return;
  }

  saving.value = true;

  try {
    form.items.forEach((item) => computeRowTotal(item));
    computeTotals();

    const payload = {
      client_id: form.client_id,
      project_id: form.project_id,
      email: form.email,
      client_address: form.client_address,
      billing_address: form.billing_address,
      estimate_date: toYmd(estimateDate.value),
      expiry_date: toYmd(expiryDate.value),
      tax_id: form.tax_id,
      tax_name: form.tax_name,
      tax_rate: form.tax_id ? form.tax_rate : 0,
      other_information: form.notes,
      status: form.status,

      items: form.items.map((i) => ({
        item: i.item,
        description: i.description,
        qty: i.qty,
        unit_cost: i.rate,
        rate: i.rate,
        discount: i.discount,
      })),

      sub_total: form.sub_total,
      discount_total: form.discount_total,
      tax_total: form.tax_total,
      grand_total: form.grand_total,
    };

    if (isEdit.value && form.id) {
      await estimateStore.update(form.id, payload);
      notifySuccess("Estimate updated");
    } else {
      await estimateStore.create(payload);
      notifySuccess("Estimate created");
    }

    addModalInstance?.hide();
    resetForm();
  } catch (e) {
    console.error("Failed to save estimate", e);
    notifyError(e?.response?.data?.message || "Failed to save estimate");
  } finally {
    saving.value = false;
  }
}

/* ---------------- DELETE ---------------- */
async function confirmDelete() {
  if (!deletingId.value) return;
  try {
    await estimateStore.remove(deletingId.value);
    deleteModalInstance?.hide();
    deletingId.value = null;
    notifySuccess("Estimate deleted");
  } catch (e) {
    console.error("Failed to delete estimate", e);
    notifyError("Failed to delete estimate");
  }
}

/* ---------------- OPEN METHODS (EXPOSED) ---------------- */
function openCreate() {
  isEdit.value = false;
  resetForm();
  addModalInstance?.show();

  if (!clientOptions.value.length) loadClientOptions().catch(() => {});
  if (taxStore && !taxOptions.value.length) loadTaxOptions().catch(() => {});
}

async function openEdit(row) {
  if (!row?.id) return;

  isEdit.value = true;
  resetForm();

  const full = row; // keeping your “no extra API call” approach

  form.id = full.id;
  form.client_id = full.client_id || full.client?.id || null;
  form.client_name = full.client_name || full.client?.name || "";
  form.project_id = full.project_id || full.project?.id || null;
  form.project_name = full.project_name || full.project?.name || "";
  form.email = full.email || full.client?.email || "";
  form.client_address = full.client_address || "";
  form.billing_address = full.billing_address || "";

  form.tax_id = full.tax_id ?? null;
  form.tax_name = full.tax_name || "";
  form.tax_rate = Number(full.tax_rate || 0);

  form.notes = full.other_information || full.notes || "";
  form.status = full.status || "Sent";

  estimateDate.value = fromYmd(full.estimate_date);
  expiryDate.value = fromYmd(full.expiry_date);

  if (Array.isArray(full.items) && full.items.length) {
    form.items = full.items.map((i) => ({
      item: i.item || "",
      description: i.description || "",
      qty: i.qty || 1,
      rate: i.rate || i.unit_cost || 0,
      discount: i.discount || 0,
      total: i.total || 0,
    }));
  }

  form.sub_total = Number(full.sub_total || 0);
  form.discount_total = Number(full.discount_total || 0);
  form.tax_total = Number(full.tax_total || 0);
  form.grand_total = Number(full.grand_total || 0);

  selectedClient.value = form.client_id
    ? {
        id: form.client_id,
        label: form.client_name || full.client?.name || "Unnamed client",
        email: full.client?.email || form.email || "",
        address: form.client_address || "",
        phone: full.client?.phone || "",
      }
    : null;

  selectedProject.value = form.project_id
    ? {
        id: form.project_id,
        label: form.project_name || full.project?.name || "",
      }
    : null;

  selectedTax.value = form.tax_id
    ? {
        id: form.tax_id,
        name: form.tax_name,
        rate: form.tax_rate,
        label: `${form.tax_name} (${form.tax_rate}%)`,
      }
    : null;

  computeTotals();
  addModalInstance?.show();

  // Load options in background for search
  loadClientOptions().catch(() => {});
  if (form.client_id) loadProjectOptions(form.client_id).catch(() => {});
  if (taxStore) loadTaxOptions().catch(() => {});
}

function openDelete(row) {
  deletingId.value = row?.id || null;
  deleteModalInstance?.show();
}

defineExpose({ openCreate, openEdit, openDelete });

/* ---------------- Click-out close for dropdowns inside modal ---------------- */
function onBodyClick(e) {
  const inDd = e.target?.closest?.(".dq-dd");
  if (!inDd) {
    ddClientOpen.value = false;
    ddProjectOpen.value = false;
    ddTaxOpen.value = false;
  }
}

/* ---------------- MOUNT ---------------- */
onMounted(() => {
  if (addModalEl.value)
    addModalInstance = new bootstrap.Modal(addModalEl.value);
  if (deleteModalEl.value)
    deleteModalInstance = new bootstrap.Modal(deleteModalEl.value);

  document.addEventListener("click", onBodyClick);

  if (!clientOptions.value.length) loadClientOptions();
  if (taxStore && !taxOptions.value.length) loadTaxOptions();
});
</script>

<template>
  <!-- ADD / EDIT ESTIMATE -->
  <div class="modal fade" id="add_estimate" tabindex="-1" ref="addModalEl">
    <div class="modal-dialog modal-dialog-centered modal-xl">
      <div class="modal-content dq-modal">
        <!-- Header -->
        <div class="modal-header dq-header">
          <div class="dq-header-left">
            <div class="dq-icon">
              <i class="ti" :class="isEdit ? 'ti-pencil' : 'ti-plus'"></i>
            </div>
            <div>
              <h4 class="modal-title mb-0">
                {{ isEdit ? "Edit Estimate" : "Create Estimate" }}
              </h4>
              <div class="dq-sub">
                Build a clean estimate with items, discounts and optional tax.
              </div>
            </div>
          </div>

          <button
            type="button"
            class="btn-close custom-btn-close"
            data-bs-dismiss="modal"
            aria-label="Close"
          >
            <i class="ti ti-x"></i>
          </button>
        </div>

        <form @submit.prevent="submit">
          <div class="modal-body dq-body">
            <div class="dq-layout">
              <!-- LEFT (Form) -->
              <div class="dq-main">
                <div class="dq-card">
                  <div class="dq-card-head">
                    <div class="dq-card-title">
                      <span class="dq-badge dq-badge-blue"
                        ><i class="ti ti-user"></i
                      ></span>
                      <div>
                        <div class="dq-h">Client & Project</div>
                        <div class="dq-sub">
                          Select a client, then optionally attach a project.
                        </div>
                      </div>
                    </div>
                  </div>

                  <div class="dq-card-body">
                    <div class="dq-grid-2">
                      <!-- Client -->
                      <div class="dq-field">
                        <label class="dq-label">
                          Client <span class="text-danger">*</span>
                        </label>

                        <div class="dq-dd">
                          <button
                            type="button"
                            class="dq-dd-btn"
                            :class="
                              attempted && errors.client_id ? 'dq-dd-err' : ''
                            "
                            @click="
                              ddClientOpen = !ddClientOpen;
                              ddProjectOpen = false;
                              ddTaxOpen = false;
                              nextTick(() => clientInputRef?.focus?.());
                            "
                          >
                            <span class="dq-dd-text">
                              {{ selectedClient?.label || "Select client" }}
                            </span>
                            <i class="ti ti-chevron-down"></i>
                          </button>

                          <div
                            v-if="ddClientOpen"
                            class="dq-dd-menu"
                            @click.stop
                          >
                            <div class="dq-dd-search">
                              <i class="ti ti-search"></i>
                              <input
                                ref="clientInputRef"
                                v-model="clientQuery"
                                @input="loadClientOptions(clientQuery)"
                                class="dq-dd-input"
                                type="text"
                                placeholder="Search clients…"
                                autocomplete="off"
                              />
                            </div>

                            <div class="dq-dd-list">
                              <div v-if="clientsLoading" class="dq-dd-empty">
                                Loading clients…
                              </div>

                              <button
                                v-for="c in clientOptions"
                                :key="c.id"
                                type="button"
                                class="dq-dd-item"
                                :class="
                                  c.id === form.client_id ? 'is-selected' : ''
                                "
                                @click="chooseClient(c)"
                              >
                                <div class="dq-dd-item-main">
                                  <div class="dq-dd-item-title">
                                    {{ c.label }}
                                  </div>
                                  <div
                                    class="dq-dd-item-sub"
                                    v-if="c.email || c.phone"
                                  >
                                    {{ c.email || c.phone }}
                                  </div>
                                </div>
                                <i
                                  v-if="c.id === form.client_id"
                                  class="ti ti-check"
                                ></i>
                              </button>

                              <div
                                v-if="!clientsLoading && !clientOptions.length"
                                class="dq-dd-empty"
                              >
                                No client found
                              </div>
                            </div>
                          </div>
                        </div>

                        <div
                          v-if="attempted && errors.client_id"
                          class="dq-err"
                        >
                          <i class="ti ti-alert-circle me-1"></i
                          >{{ errors.client_id }}
                        </div>
                      </div>

                      <!-- Project -->
                      <div class="dq-field">
                        <label class="dq-label">Project</label>

                        <div class="dq-dd">
                          <button
                            type="button"
                            class="dq-dd-btn"
                            :disabled="!form.client_id"
                            @click="
                              ddProjectOpen = !ddProjectOpen;
                              ddClientOpen = false;
                              ddTaxOpen = false;
                              nextTick(() => projectInputRef?.focus?.());
                            "
                          >
                            <span class="dq-dd-text">
                              {{
                                selectedProject?.label ||
                                (form.client_id
                                  ? "Select project"
                                  : "Select client first")
                              }}
                            </span>
                            <i class="ti ti-chevron-down"></i>
                          </button>

                          <div
                            v-if="ddProjectOpen"
                            class="dq-dd-menu"
                            @click.stop
                          >
                            <div class="dq-dd-search">
                              <i class="ti ti-search"></i>
                              <input
                                ref="projectInputRef"
                                v-model="projectQuery"
                                @input="
                                  loadProjectOptions(
                                    form.client_id,
                                    projectQuery
                                  )
                                "
                                class="dq-dd-input"
                                type="text"
                                placeholder="Search projects…"
                                autocomplete="off"
                                :disabled="!form.client_id"
                              />
                            </div>

                            <div class="dq-dd-list">
                              <div v-if="projectsLoading" class="dq-dd-empty">
                                Loading projects…
                              </div>

                              <button
                                v-for="p in projectOptions"
                                :key="p.id"
                                type="button"
                                class="dq-dd-item"
                                :class="
                                  p.id === form.project_id ? 'is-selected' : ''
                                "
                                @click="chooseProject(p)"
                              >
                                <div class="dq-dd-item-main">
                                  <div class="dq-dd-item-title">
                                    {{ p.label }}
                                  </div>
                                </div>
                                <i
                                  v-if="p.id === form.project_id"
                                  class="ti ti-check"
                                ></i>
                              </button>

                              <div
                                v-if="
                                  !projectOptions.length && !projectsLoading
                                "
                                class="dq-dd-empty"
                              >
                                No project found
                              </div>
                            </div>
                          </div>
                        </div>

                        <div class="dq-help">Optional.</div>
                      </div>
                    </div>

                    <div class="dq-grid-2 mt-2">
                      <!-- Email -->
                      <div class="dq-field">
                        <label class="dq-label">
                          Email <span class="text-danger">*</span>
                        </label>
                        <input
                          type="email"
                          class="form-control dq-input"
                          v-model="form.email"
                          placeholder="client@example.com"
                          :class="attempted && errors.email ? 'is-invalid' : ''"
                        />
                        <div v-if="attempted && errors.email" class="dq-err">
                          <i class="ti ti-alert-circle me-1"></i
                          >{{ errors.email }}
                        </div>
                      </div>

                      <!-- Status -->
                      <div class="dq-field">
                        <label class="dq-label">Status</label>

                        <vue-select
                          v-model="form.status"
                          :options="['Draft', 'Sent', 'Accepted', 'Rejected']"
                          placeholder="Select Status"
                          class="dq-vselect"
                          :clearable="false"
                        />

                        <div class="dq-help">
                          Pick what your workflow expects.
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Addresses -->
                <div class="dq-card">
                  <div class="dq-card-head">
                    <div class="dq-card-title">
                      <span class="dq-badge dq-badge-slate"
                        ><i class="ti ti-map-pin"></i
                      ></span>
                      <div>
                        <div class="dq-h">Addresses</div>
                        <div class="dq-sub">Used in PDF / email templates.</div>
                      </div>
                    </div>
                  </div>

                  <div class="dq-card-body">
                    <div class="dq-grid-2">
                      <div class="dq-field">
                        <label class="dq-label">Client Address</label>
                        <textarea
                          class="form-control dq-textarea"
                          rows="3"
                          v-model="form.client_address"
                        />
                      </div>

                      <div class="dq-field">
                        <label class="dq-label">Billing Address</label>
                        <textarea
                          class="form-control dq-textarea"
                          rows="3"
                          v-model="form.billing_address"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Dates + Tax -->
                <div class="dq-card">
                  <div class="dq-card-head">
                    <div class="dq-card-title">
                      <span class="dq-badge dq-badge-amber"
                        ><i class="ti ti-calendar"></i
                      ></span>
                      <div>
                        <div class="dq-h">Dates & Tax</div>
                        <div class="dq-sub">
                          Choose estimate dates and optional tax.
                        </div>
                      </div>
                    </div>

                    <button
                      v-if="form.tax_id"
                      type="button"
                      class="btn btn-light dq-mini-btn"
                      @click="clearTax"
                    >
                      <i class="ti ti-x me-1"></i>Clear Tax
                    </button>
                  </div>

                  <div class="dq-card-body">
                    <div class="dq-grid-2">
                      <div class="dq-field">
                        <label class="dq-label">Estimate Date</label>
                        <div class="dq-date">
                          <span class="dq-date-ic"
                            ><i class="ti ti-calendar"></i
                          ></span>
                          <date-picker
                            v-model="estimateDate"
                            class="form-control datetimepicker dq-input"
                            placeholder="dd/mm/yyyy"
                            :editable="true"
                            :clearable="false"
                            input-format="dd-MM-yyyy"
                          />
                        </div>
                      </div>

                      <div class="dq-field">
                        <label class="dq-label">Expiry Date</label>
                        <div class="dq-date">
                          <span class="dq-date-ic"
                            ><i class="ti ti-calendar"></i
                          ></span>
                          <date-picker
                            v-model="expiryDate"
                            class="form-control datetimepicker dq-input"
                            placeholder="dd/mm/yyyy"
                            :editable="true"
                            :clearable="false"
                            input-format="dd-MM-yyyy"
                          />
                        </div>
                      </div>
                    </div>

                    <div class="dq-field mt-2">
                      <label class="dq-label">Tax</label>
                      <div class="dq-dd">
                        <button
                          type="button"
                          class="dq-dd-btn"
                          @click="
                            ddTaxOpen = !ddTaxOpen;
                            ddClientOpen = false;
                            ddProjectOpen = false;
                            nextTick(() => taxInputRef?.focus?.());
                          "
                        >
                          <span class="dq-dd-text">
                            {{
                              selectedTax
                                ? `${selectedTax.name} (${selectedTax.rate}%)`
                                : "Select Tax"
                            }}
                          </span>
                          <i class="ti ti-chevron-down"></i>
                        </button>

                        <div v-if="ddTaxOpen" class="dq-dd-menu" @click.stop>
                          <div class="dq-dd-search">
                            <i class="ti ti-search"></i>
                            <input
                              ref="taxInputRef"
                              v-model="taxQuery"
                              @input="loadTaxOptions(taxQuery)"
                              class="dq-dd-input"
                              type="text"
                              placeholder="Search tax…"
                              autocomplete="off"
                            />
                          </div>

                          <div class="dq-dd-list">
                            <div v-if="taxesLoading" class="dq-dd-empty">
                              Loading taxes…
                            </div>

                            <button
                              v-for="t in taxOptions"
                              :key="t.id"
                              type="button"
                              class="dq-dd-item"
                              :class="t.id === form.tax_id ? 'is-selected' : ''"
                              @click="chooseTax(t)"
                            >
                              <div class="dq-dd-item-main">
                                <div class="dq-dd-item-title">
                                  {{ t.label }}
                                </div>
                                <div
                                  class="dq-dd-item-sub"
                                  v-if="t.description"
                                >
                                  {{ t.description }}
                                </div>
                              </div>
                              <i
                                v-if="t.id === form.tax_id"
                                class="ti ti-check"
                              ></i>
                            </button>

                            <div
                              v-if="!taxesLoading && !taxOptions.length"
                              class="dq-dd-empty"
                            >
                              No tax found
                            </div>
                          </div>
                        </div>
                      </div>

                      <div class="dq-help">
                        Tax is calculated on (Subtotal - Discount).
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Items -->
                <div class="dq-card">
                  <div class="dq-card-head">
                    <div class="dq-card-title">
                      <span class="dq-badge dq-badge-green"
                        ><i class="ti ti-shopping-cart"></i
                      ></span>
                      <div>
                        <div class="dq-h">Items</div>
                        <div class="dq-sub">
                          Clean, table-like item entry for speed.
                        </div>
                      </div>
                    </div>

                    <button
                      type="button"
                      class="btn btn-outline-primary dq-mini-btn"
                      @click.prevent="addItemRow"
                    >
                      <i class="ti ti-plus me-1"></i>Add item
                    </button>
                  </div>

                  <div class="dq-card-body">
                    <div v-if="attempted && errors.items" class="dq-err mb-2">
                      <i class="ti ti-alert-circle me-1"></i>{{ errors.items }}
                    </div>

                    <div class="dq-items">
                      <div class="dq-items-head">
                        <div>Item</div>
                        <div>Description</div>
                        <div class="text-end">Unit Cost</div>
                        <div class="text-end">Qty</div>
                        <div class="text-end">Discount</div>
                        <div class="text-end">Amount</div>
                        <div></div>
                      </div>

                      <div
                        v-for="(item, idx) in form.items"
                        :key="idx"
                        class="dq-items-row"
                      >
                        <div class="dq-cell">
                          <input
                            class="form-control dq-input"
                            type="text"
                            v-model="item.item"
                            placeholder="e.g. Setup, Subscription"
                          />
                        </div>

                        <div class="dq-cell">
                          <input
                            :class="[
                              'form-control dq-input',
                              idx === form.items.length - 1
                                ? 'dq-est-last-desc'
                                : '',
                            ]"
                            type="text"
                            v-model="item.description"
                            placeholder="Description…"
                            @input="
                              () => {
                                computeRowTotal(item);
                                computeTotals();
                              }
                            "
                          />
                        </div>

                        <div class="dq-cell">
                          <input
                            class="form-control dq-input dq-right"
                            type="number"
                            min="0"
                            v-model.number="item.rate"
                            @input="
                              () => {
                                computeRowTotal(item);
                                computeTotals();
                              }
                            "
                          />
                        </div>

                        <div class="dq-cell">
                          <input
                            class="form-control dq-input dq-right"
                            type="number"
                            min="0"
                            v-model.number="item.qty"
                            @input="
                              () => {
                                computeRowTotal(item);
                                computeTotals();
                              }
                            "
                          />
                        </div>

                        <div class="dq-cell">
                          <input
                            class="form-control dq-input dq-right"
                            type="number"
                            min="0"
                            v-model.number="item.discount"
                            @input="
                              () => {
                                computeRowTotal(item);
                                computeTotals();
                              }
                            "
                          />
                        </div>

                        <div class="dq-cell">
                          <input
                            class="form-control dq-input dq-right"
                            type="text"
                            :value="formatMoney(item.total)"
                            disabled
                          />
                        </div>

                        <div class="dq-cell dq-actions">
                          <button
                            v-if="form.items.length > 1"
                            type="button"
                            class="btn btn-light dq-icon-btn"
                            title="Remove"
                            @click="removeItemRow(idx)"
                          >
                            <i class="ti ti-trash"></i>
                          </button>
                        </div>
                      </div>
                    </div>

                    <button
                      type="button"
                      class="dq-addline"
                      @click.prevent="addItemRow"
                    >
                      <i class="ti ti-plus me-2"></i>Add another line
                    </button>
                  </div>
                </div>

                <!-- Notes -->
                <div class="dq-card">
                  <div class="dq-card-head">
                    <div class="dq-card-title">
                      <span class="dq-badge dq-badge-purple"
                        ><i class="ti ti-notes"></i
                      ></span>
                      <div>
                        <div class="dq-h">Other Information</div>
                        <div class="dq-sub">
                          Optional notes that appear on estimate.
                        </div>
                      </div>
                    </div>
                  </div>

                  <div class="dq-card-body">
                    <textarea
                      class="form-control dq-textarea"
                      rows="4"
                      v-model="form.notes"
                    />
                  </div>
                </div>
              </div>

              <!-- RIGHT (Sticky Totals) -->
              <div class="dq-side">
                <div class="dq-sticky">
                  <div class="dq-card dq-summary">
                    <div class="dq-card-head">
                      <div class="dq-card-title">
                        <span class="dq-badge dq-badge-dark"
                          ><i class="ti ti-calculator"></i
                        ></span>
                        <div>
                          <div class="dq-h">Summary</div>
                          <div class="dq-sub">Totals update instantly.</div>
                        </div>
                      </div>
                    </div>

                    <div class="dq-card-body">
                      <div class="dq-kv">
                        <div class="dq-k">Sub Total</div>
                        <div class="dq-v">
                          {{ formatMoney(form.sub_total) }}
                        </div>
                      </div>

                      <div class="dq-kv">
                        <div class="dq-k">Discount</div>
                        <div class="dq-v">
                          - {{ formatMoney(form.discount_total) }}
                        </div>
                      </div>

                      <div class="dq-kv" v-if="form.tax_id">
                        <div class="dq-k">Tax ({{ form.tax_rate }}%)</div>
                        <div class="dq-v">
                          {{ formatMoney(form.tax_total) }}
                        </div>
                      </div>

                      <div class="dq-divider"></div>

                      <div class="dq-kv dq-kv-total">
                        <div class="dq-k">Grand Total</div>
                        <div class="dq-v">{{ grandTotalLabel }}</div>
                      </div>

                      <div class="dq-actions-stack mt-3">
                        <button
                          type="submit"
                          class="btn btn-primary w-100 dq-action-btn"
                          :disabled="saving || !canSubmit"
                        >
                          <span
                            v-if="saving"
                            class="spinner-border spinner-border-sm me-2"
                          ></span>
                          <i v-else class="ti ti-device-floppy me-2"></i>
                          {{ isEdit ? "Update Estimate" : "Create Estimate" }}
                        </button>

                        <div v-if="attempted && !canSubmit" class="dq-err mt-2">
                          <i class="ti ti-alert-circle me-1"></i>Please fix the
                          form errors to continue.
                        </div>

                        <button
                          type="button"
                          class="btn btn-light w-100 dq-action-btn"
                          data-bs-dismiss="modal"
                        >
                          <i class="ti ti-x me-2"></i>Cancel
                        </button>
                      </div>
                    </div>
                  </div>

                  <div class="dq-card dq-hint mt-3">
                    <div class="dq-card-body">
                      <div class="dq-hint-row">
                        <i class="ti ti-bulb"></i>
                        <div>
                          <div class="dq-hint-title">Tip</div>
                          <div class="dq-hint-text">
                            Use the “Project” to keep estimates grouped and
                            easier to convert into invoices.
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Footer -->
          <div class="modal-footer dq-footer">
            <button type="button" class="btn btn-light" data-bs-dismiss="modal">
              Cancel
            </button>
            <button
              type="submit"
              class="btn btn-primary"
              :disabled="saving || !canSubmit"
            >
              <span
                v-if="saving"
                class="spinner-border spinner-border-sm me-1"
              />
              {{ isEdit ? "Update Estimate" : "Create Estimate" }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>

  <!-- DELETE MODAL -->
  <div class="modal fade" id="delete_modal" tabindex="-1" ref="deleteModalEl">
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-body text-center">
          <span class="avatar avatar-xl bg-transparent-danger text-danger mb-3">
            <i class="ti ti-trash-x fs-36"></i>
          </span>
          <h4 class="mb-1">Confirm Delete</h4>
          <p class="mb-3">
            You want to delete this estimate, this can't be undone once you
            delete.
          </p>

          <div class="d-flex justify-content-center">
            <button
              type="button"
              class="btn btn-light me-3"
              data-bs-dismiss="modal"
            >
              Cancel
            </button>
            <button type="button" class="btn btn-danger" @click="confirmDelete">
              <span
                v-if="estimateStore.loadingForDelete"
                class="spinner-border spinner-border-sm me-1"
              />
              Yes, Delete
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* ========== Premium modal sizing ========== */
.dq-modal {
  border-radius: 18px;
  overflow: hidden;
  border: 1px solid rgba(0, 0, 0, 0.06);
  box-shadow: 0 30px 80px rgba(16, 24, 40, 0.18);
}
.dq-header {
  background: linear-gradient(
    180deg,
    rgba(248, 250, 252, 1),
    rgba(255, 255, 255, 1)
  );
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}
.dq-header-left {
  display: flex;
  align-items: center;
  gap: 12px;
}
.dq-icon {
  width: 44px;
  height: 44px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(37, 99, 235, 0.1);
  color: rgba(37, 99, 235, 1);
}
.dq-sub {
  font-size: 0.86rem;
  color: rgba(17, 24, 39, 0.6);
  margin-top: 2px;
}

.dq-body {
  background: rgba(248, 250, 252, 0.55);
}
.dq-footer {
  background: #fff;
  border-top: 1px solid rgba(0, 0, 0, 0.06);
}

/* Layout inside modal */
.dq-layout {
  display: grid;
  grid-template-columns: 1fr 360px;
  gap: 14px;
}
@media (max-width: 992px) {
  .dq-layout {
    grid-template-columns: 1fr;
  }
}
.dq-sticky {
  position: sticky;
  top: 10px;
}

/* cards */
.dq-card {
  background: #fff;
  border: 1px solid rgba(0, 0, 0, 0.06);
  border-radius: 18px;
  box-shadow: 0 16px 40px rgba(16, 24, 40, 0.06);
  overflow: hidden;
  margin-bottom: 14px;
}
.dq-card-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 14px 16px;
  background: linear-gradient(
    180deg,
    rgba(248, 250, 252, 1),
    rgba(255, 255, 255, 1)
  );
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}
.dq-card-body {
  padding: 14px 16px;
}

.dq-card-title {
  display: flex;
  align-items: center;
  gap: 10px;
}
.dq-h {
  font-weight: 950;
  letter-spacing: -0.02em;
  color: rgba(17, 24, 39, 0.92);
}
.dq-mini-btn {
  border-radius: 14px;
  font-weight: 900;
}

/* badges */
.dq-badge {
  width: 38px;
  height: 38px;
  border-radius: 14px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(59, 130, 246, 0.12);
  color: rgba(37, 99, 235, 1);
}
.dq-badge-blue {
  background: rgba(59, 130, 246, 0.12);
  color: rgba(37, 99, 235, 1);
}
.dq-badge-green {
  background: rgba(16, 185, 129, 0.12);
  color: rgba(5, 150, 105, 1);
}
.dq-badge-amber {
  background: rgba(245, 158, 11, 0.14);
  color: rgba(217, 119, 6, 1);
}
.dq-badge-slate {
  background: rgba(148, 163, 184, 0.18);
  color: rgba(51, 65, 85, 1);
}
.dq-badge-purple {
  background: rgba(99, 102, 241, 0.12);
  color: rgba(79, 70, 229, 1);
}
.dq-badge-dark {
  background: rgba(17, 24, 39, 0.08);
  color: rgba(17, 24, 39, 0.9);
}

/* fields */
.dq-grid-2 {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
}
@media (max-width: 992px) {
  .dq-grid-2 {
    grid-template-columns: 1fr;
  }
}

.dq-label {
  font-weight: 900;
  color: rgba(17, 24, 39, 0.9);
  margin-bottom: 6px;
}
.dq-input {
  border-radius: 14px !important;
  border: 1px solid rgba(0, 0, 0, 0.08) !important;
  min-height: 44px;
}
.dq-textarea {
  border-radius: 14px !important;
  border: 1px solid rgba(0, 0, 0, 0.08) !important;
}
.dq-right {
  text-align: right;
}
.dq-help {
  font-size: 0.82rem;
  color: rgba(17, 24, 39, 0.6);
  margin-top: 6px;
}

/* date */
.dq-date {
  position: relative;
}
.dq-date-ic {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: rgba(17, 24, 39, 0.45);
  z-index: 3;
  pointer-events: none;
}
.dq-date :deep(.datetimepicker) {
  padding-left: 40px !important;
}

/* dropdown custom */
.dq-dd {
  position: relative;
}
.dq-dd-btn {
  width: 100%;
  min-height: 44px;
  border-radius: 14px;
  border: 1px solid rgba(0, 0, 0, 0.08);
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 10px 12px;
  font-weight: 800;
  color: rgba(17, 24, 39, 0.85);
}
.dq-dd-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
.dq-dd-text {
  flex: 1;
  text-align: left;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.dq-dd-err {
  border-color: rgba(220, 38, 38, 0.35);
  box-shadow: 0 0 0 4px rgba(220, 38, 38, 0.1);
}

.dq-dd-menu {
  position: absolute;
  z-index: 60;
  width: 100%;
  margin-top: 8px;
  border-radius: 16px;
  border: 1px solid rgba(0, 0, 0, 0.06);
  background: #fff;
  box-shadow: 0 24px 60px rgba(16, 24, 40, 0.18);
  overflow: hidden;
}
.dq-dd-search {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  background: rgba(248, 250, 252, 1);
  color: rgba(17, 24, 39, 0.55);
}
.dq-dd-input {
  border: 0;
  outline: none;
  background: transparent;
  width: 100%;
  font-weight: 800;
  color: rgba(17, 24, 39, 0.85);
}
.dq-dd-list {
  max-height: 260px;
  overflow: auto;
  padding: 6px;
}
.dq-dd-item {
  width: 100%;
  border: 0;
  background: transparent;
  text-align: left;
  padding: 10px 10px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  transition: background 0.12s ease;
}
.dq-dd-item:hover {
  background: rgba(37, 99, 235, 0.08);
}
.dq-dd-item.is-selected {
  background: rgba(16, 185, 129, 0.1);
}
.dq-dd-item-main {
  min-width: 0;
}
.dq-dd-item-title {
  font-weight: 900;
  color: rgba(17, 24, 39, 0.9);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.dq-dd-item-sub {
  font-size: 0.78rem;
  color: rgba(17, 24, 39, 0.55);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.dq-dd-empty {
  padding: 14px 10px;
  text-align: center;
  color: rgba(17, 24, 39, 0.55);
  font-size: 0.85rem;
}

/* items */
.dq-items-head {
  display: grid;
  grid-template-columns: 1.1fr 1.5fr 0.9fr 0.6fr 0.9fr 1fr 44px;
  gap: 10px;
  font-size: 0.78rem;
  font-weight: 950;
  color: rgba(17, 24, 39, 0.55);
  padding: 6px 2px 10px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  margin-bottom: 10px;
}
.dq-items-row {
  display: grid;
  grid-template-columns: 1.1fr 1.5fr 0.9fr 0.6fr 0.9fr 1fr 44px;
  gap: 10px;
  margin-bottom: 10px;
}
.dq-actions {
  display: flex;
  justify-content: flex-end;
}
.dq-icon-btn {
  border-radius: 12px;
  width: 40px;
  height: 40px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
@media (max-width: 992px) {
  .dq-items-head {
    display: none;
  }
  .dq-items-row {
    grid-template-columns: 1fr;
    border: 1px solid rgba(0, 0, 0, 0.06);
    border-radius: 16px;
    padding: 12px;
    gap: 12px;
  }
  .dq-actions {
    justify-content: flex-start;
  }
}
.dq-addline {
  width: 100%;
  margin-top: 6px;
  border-radius: 14px;
  border: 1px dashed rgba(37, 99, 235, 0.35);
  background: rgba(37, 99, 235, 0.06);
  padding: 10px 12px;
  font-weight: 900;
  color: rgba(37, 99, 235, 1);
  display: flex;
  align-items: center;
  justify-content: center;
}

/* summary */
.dq-kv {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 0;
}
.dq-k {
  color: rgba(17, 24, 39, 0.6);
  font-weight: 800;
}
.dq-v {
  font-weight: 950;
  color: rgba(17, 24, 39, 0.92);
}
.dq-divider {
  height: 1px;
  background: rgba(0, 0, 0, 0.06);
  margin: 10px 0;
}
.dq-kv-total .dq-v {
  font-size: 1.15rem;
}

.dq-action-btn {
  border-radius: 14px;
  padding: 12px 14px;
  font-weight: 900;
}

/* errors */
.dq-err {
  margin-top: 8px;
  font-size: 0.84rem;
  font-weight: 900;
  color: rgba(220, 38, 38, 1);
  display: flex;
  align-items: center;
}
.dq-hint-row {
  display: flex;
  gap: 10px;
  align-items: flex-start;
  color: rgba(17, 24, 39, 0.7);
}
.dq-hint-title {
  font-weight: 950;
  color: rgba(17, 24, 39, 0.9);
  margin-bottom: 2px;
}
.dq-hint-text {
  font-size: 0.85rem;
  color: rgba(17, 24, 39, 0.6);
}

/* FORCE BOOTSTRAP MODAL BODY TO SCROLL */
/* ✅ SINGLE source of truth for scrolling */
:deep(#add_estimate .modal-dialog) {
  height: calc(100vh - 2rem) !important;
  margin: 1rem auto !important;
}

:deep(#add_estimate .modal-dialog.modal-dialog-centered) {
  align-items: stretch !important; /* IMPORTANT: don't vertically center the dialog box */
}

:deep(#add_estimate .modal-content) {
  height: 100% !important;
  max-height: 100% !important;
  display: flex !important;
  flex-direction: column !important;
}

/* Your premium container */
:deep(#add_estimate .dq-modal) {
  height: 100% !important;
  max-height: 100% !important;
  display: flex !important;
  flex-direction: column !important;
  overflow: hidden !important; /* keep rounded corners */
}

/* ✅ THIS is the scroll container */
:deep(#add_estimate .modal-body.dq-body) {
  flex: 1 1 auto !important;
  min-height: 0 !important;
  overflow-y: auto !important;
  -webkit-overflow-scrolling: touch;
}

:deep(#add_estimate .modal-content) {
  max-height: calc(100vh - 2rem); /* key */
  display: flex; /* key */
  flex-direction: column; /* key */
}

:deep(#add_estimate .modal-body) {
  overflow-y: auto !important; /* key */
  -webkit-overflow-scrolling: touch;
  flex: 1 1 auto; /* key */
  min-height: 0; /* key */
}

/* ✅ keep modal content clipped, but allow the BODY to scroll */
:deep(#add_estimate .dq-modal) {
  overflow: hidden !important; /* ok for rounded corners */
  display: flex;
  flex-direction: column;
  max-height: calc(100vh - 2rem);
}

:deep(#add_estimate .dq-body) {
  overflow-y: auto !important; /* ✅ THIS enables scrolling */

  -webkit-overflow-scrolling: touch;
  flex: 1 1 auto;
  min-height: 0;
}

@media (max-width: 992px) {
  :deep(#add_estimate .dq-sticky) {
    position: static !important;
  }
}
</style>
